import { iterateDateKeys } from './dashboardDates';

export type DayOfWeekKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export const DAY_OF_WEEK_KEYS: DayOfWeekKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
];

/** Stored shape: enabled flag + planned hours per weekday. */
export type PersonalDaySchedule = {
  enabled: boolean;
  hours: number;
};

export type PersonalWorkingHoursSchedule = Record<DayOfWeekKey, PersonalDaySchedule>;

/** Legacy preference rows may still carry mode/from/to from older UI. */
type LegacyDayRow = {
  enabled?: boolean;
  hours?: number;
  mode?: string;
  from?: string;
  to?: string;
};

function parseHHmm(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hh = parseInt(match[1]!, 10);
  const mm = parseInt(match[2]!, 10);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function legacyRangeHours(row: LegacyDayRow): number {
  const fromMin = parseHHmm(row.from);
  const toMin = parseHHmm(row.to);
  if (fromMin !== null && toMin !== null && toMin > fromMin) {
    return (toMin - fromMin) / 60;
  }
  return 0;
}

export function normalizePersonalDaySchedule(raw: unknown): PersonalDaySchedule {
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, hours: 0 };
  }
  const row = raw as LegacyDayRow;
  const enabled = !!row.enabled;
  if (!enabled) {
    return { enabled: false, hours: 0 };
  }

  let hours = typeof row.hours === 'number' ? row.hours : parseFloat(String(row.hours ?? ''));
  if (!Number.isFinite(hours) || hours <= 0) {
    hours = legacyRangeHours(row);
  }

  return { enabled: true, hours: Math.max(0, hours) };
}

export function normalizePersonalWorkingHoursSchedule(raw: unknown): PersonalWorkingHoursSchedule {
  const src = raw && typeof raw === 'object' ? (raw as Partial<Record<DayOfWeekKey, unknown>>) : {};
  const out = {} as PersonalWorkingHoursSchedule;
  for (const key of DAY_OF_WEEK_KEYS) {
    out[key] = normalizePersonalDaySchedule(src[key]);
  }
  return out;
}

export function dateKeyToDayOfWeek(dateKey: string): DayOfWeekKey {
  const d = new Date(dateKey + 'T12:00:00');
  switch (d.getDay()) {
    case 1:
      return 'monday';
    case 2:
      return 'tuesday';
    case 3:
      return 'wednesday';
    case 4:
      return 'thursday';
    case 5:
      return 'friday';
    case 6:
      return 'saturday';
    case 0:
    default:
      return 'sunday';
  }
}

/** Planned hours for one day from user preference schedule. */
export function plannedHoursForDay(cfg: unknown): number {
  const day = normalizePersonalDaySchedule(cfg);
  return day.enabled ? day.hours : 0;
}

/** Sum planned hours across an inclusive local date range. */
export function sumPlannedHoursForRange(
  schedule: unknown,
  from: string,
  to: string
): number {
  const normalized = normalizePersonalWorkingHoursSchedule(schedule);
  let total = 0;
  for (const dateKey of iterateDateKeys(from, to)) {
    const dayKey = dateKeyToDayOfWeek(dateKey);
    total += plannedHoursForDay(normalized[dayKey]);
  }
  return total;
}
